import React from 'react';
import { Download, Loader2, Mail, Printer, RotateCcw } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { buildDocumentFilename } from '../../lib/documentFilename';
import { mentionsLegalesManquantes, messageMentionsManquantes } from '../../lib/mentionsLegales';
import { InvoiceForm } from './InvoiceForm';
import { InvoiceDocument } from './InvoiceDocument';
import { InvoiceSendModal } from './InvoiceSendModal';
import { useInvoiceEditor } from './useInvoiceEditor';
import {
    openInvoicePrintWindow,
    generateInvoicePdfFromElement,
    INVOICE_SCREEN_PRINT_CSS,
} from './invoicePdfExport';

// Correction : recordFactureAction n'existe pas dans ../../lib/api
// On ajoute une version locale mock qui fait juste un appel à l'API d'historique si besoin
async function recordFactureAction(factureId: string, clientId: string, action: string, email?: string) {
    // Ici, on peut faire un POST vers une route d'API si besoin, ou juste un no-op pour éviter l'erreur
    // Par exemple : await apiFetch(`/facture/${factureId}/history`, { method: 'POST', body: JSON.stringify({ clientId, action, email }) });
    return;
}

interface CreateInvoiceProps {
    onInvoiceSaved?: () => void;
    initialInvoice?: any;
    autoPrintToken?: number;
}

/**
 * L'écran de création d'une facture : la saisie à gauche, le document à droite, et ce qu'on en fait
 * (imprimer, télécharger, envoyer).
 *
 * L'état de la facture vit dans useInvoiceEditor, le document dans InvoiceDocument, la saisie dans
 * InvoiceForm — ce fichier n'orchestre que les actions.
 */
export const CreateInvoice = ({ onInvoiceSaved, initialInvoice, autoPrintToken, autoDownloadToken, silentMode, onDownloadComplete }: CreateInvoiceProps & { autoDownloadToken?: number; silentMode?: boolean; onDownloadComplete?: () => void }) => {
    const editor = useInvoiceEditor(initialInvoice);
    const {
        user, settings, invoiceNumber, recipientEmail, setRecipientEmail,
        currentInvoiceId, setCurrentInvoiceId, dataLoaded, previewHasContent,
        buildInvoicePayload, resetAll, documentProps, formProps,
    } = editor;

    const [emailModalOpen, setEmailModalOpen] = React.useState(false);
    const [sendingEmail, setSendingEmail] = React.useState(false);
    const [savingInvoice, setSavingInvoice] = React.useState(false);
    const printAreaRef = React.useRef<HTMLDivElement | null>(null);

    // Mentions légales obligatoires manquantes : aucune facture ne s'imprime, se télécharge ni s'envoie, et rien
    // n'est enregistré, tant qu'elles ne sont pas complétées (contrôlé avant de numéroter la facture).
    const mentionsManquantes = React.useMemo(
        () => (dataLoaded ? mentionsLegalesManquantes({ ...settings, company_name: settings?.company_name || user?.company_name || user?.name }) : []),
        [dataLoaded, settings, user],
    );
    const bloqueMentionsLegales = () => {
        if (mentionsManquantes.length === 0) return false;
        alert(messageMentionsManquantes('facture', mentionsManquantes));
        return true;
    };

    const openPrintWindow = React.useCallback((targetWindow?: Window | null) => {
        openInvoicePrintWindow(printAreaRef.current, invoiceNumber, targetWindow);
    }, [invoiceNumber]);

    const saveCurrentInvoice = async (payloadOverride?: ReturnType<typeof buildInvoicePayload>) => {
        setSavingInvoice(true);
        try {
            const payload = payloadOverride || buildInvoicePayload();
            const saved = await moduleApi.createFacture(payload);
            setCurrentInvoiceId(String(saved?.id || payload.id));
            await onInvoiceSaved?.();
            return saved || payload;
        } catch (e: any) {
            alert(e?.message || 'Erreur lors de l’enregistrement de la facture');
            return null;
        } finally {
            setSavingInvoice(false);
        }
    };

    const handlePrintInvoice = async () => {
        if (bloqueMentionsLegales()) return;
        const popup = window.open('', '_blank', 'width=900,height=1200');
        if (!popup) {
            alert('Veuillez autoriser la fenêtre d’impression.');
            return;
        }
        let saved: any = null;
        // On ne sauvegarde que si c'est une nouvelle facture
        if (!currentInvoiceId) {
            saved = await saveCurrentInvoice();
            if (!saved) {
                popup.close();
                return;
            }
        }
        // Enregistrement action print
        try {
            const finalId = currentInvoiceId || saved?.id;
            if (finalId && user?.clientId) {
                await recordFactureAction(String(finalId), String(user.clientId), 'print');
            }
        } catch (e) {
            // Optionnel : afficher une erreur ou ignorer
        }
        openPrintWindow(popup);
    };

    const handleOpenSendModal = () => {
        if (bloqueMentionsLegales()) return;
        setEmailModalOpen(true);
    };

    const generatePreviewPdfDocument = async () => generateInvoicePdfFromElement(printAreaRef.current);

    const generatePreviewPdfBase64 = async () => {
        const pdf = await generatePreviewPdfDocument();
        // datauristring retourne "data:application/pdf;base64,..." — on extrait la partie base64
        const dataUri = pdf.output('datauristring') as string;
        return dataUri.split(',')[1];
    };

    const handleDownloadPdf = async () => {
        if (bloqueMentionsLegales()) return;
        if (!previewHasContent) {
            alert('La facture est vide. Veuillez saisir des montants avant de télécharger.');
            return;
        }

        let saved: any = null;
        // On ne sauvegarde que si c'est une nouvelle facture
        if (!currentInvoiceId) {
            saved = await saveCurrentInvoice();
            if (!saved) return;
        }

        // Enregistrement action download
        try {
            const finalId = currentInvoiceId || saved?.id;
            if (finalId && user?.clientId) {
                await recordFactureAction(String(finalId), String(user.clientId), 'download');
            }
        } catch (e) {
            // Optionnel : afficher une erreur ou ignorer
        }

        try {
            const pdf = await generatePreviewPdfDocument();
            const companyNameForFile = settings.company_name || user?.company_name || user?.name || '';
            pdf.save(buildDocumentFilename(companyNameForFile, 'FACTURE', invoiceNumber));
        } catch (e: any) {
            alert(e?.message || 'Erreur lors du téléchargement du PDF');
        }
    };

    const handleSendInvoiceEmail = async () => {
        if (bloqueMentionsLegales()) return;
        const email = recipientEmail.trim();
        if (!email) {
            alert('Veuillez saisir une adresse email.');
            return;
        }
        if (!previewHasContent) {
            alert('La facture est vide. Veuillez saisir des montants avant de l\'envoyer.');
            return;
        }

        setSendingEmail(true);
        setEmailModalOpen(false);

        try {
            let payload = buildInvoicePayload();
            // La facture doit exister en base avant l'envoi (la route /facture/:id/send-email vérifie
            // que l'id existe réellement pour cet établissement) — comme pour l'impression et le
            // téléchargement PDF, on l'enregistre d'abord si ce n'est pas déjà fait.
            if (!currentInvoiceId) {
                const saved = await saveCurrentInvoice(payload);
                if (!saved) return;
                payload = { ...payload, id: String(saved.id || payload.id) };
            }
            const invoiceId = String(currentInvoiceId || payload.id || '');
            if (!invoiceId) {
                alert('Impossible de préparer la facture pour l’envoi.');
                return;
            }

            // Générer le PDF côté frontend comme avant
            const pdfBase64 = await generatePreviewPdfBase64();
            const filename = buildDocumentFilename(settings.company_name || user?.company_name || user?.name || '', 'FACTURE', payload.invoiceNumber);
            await moduleApi.sendFactureEmail(invoiceId, { to: email, pdfBase64, filename, invoicePayload: payload });
            await saveCurrentInvoice(payload);
            // Enregistrement action email dans l'historique
            if (invoiceId && user?.clientId) {
                await recordFactureAction(invoiceId, String(user.clientId), 'email', email);
            }
            // Rafraîchir l'historique si callback fourni
            if (typeof onInvoiceSaved === 'function') onInvoiceSaved();
        } catch (e: any) {
            console.error('Erreur envoi facture:', e);
            alert('Erreur lors de l\'envoi : ' + (e?.message || String(e) || 'Erreur inconnue'));
        } finally {
            setSendingEmail(false);
        }
    };

    const handleReset = () => {
        setEmailModalOpen(false);
        resetAll();
    };

    React.useEffect(() => {
        if (!autoPrintToken || !dataLoaded || !previewHasContent) return;
        const timer = window.setTimeout(() => openPrintWindow(), 500);
        return () => window.clearTimeout(timer);
    }, [autoPrintToken, openPrintWindow, dataLoaded, previewHasContent]);

    React.useEffect(() => {
        if (!autoDownloadToken) return;
        // Si on est en train de charger, on attend un peu
        if (!dataLoaded || !previewHasContent) {
            const timeout = window.setTimeout(() => {
                if (!dataLoaded || !previewHasContent) {
                    onDownloadComplete?.();
                }
            }, 5000); // 5s max avant d'abandonner le mode silencieux
            return () => window.clearTimeout(timeout);
        }

        const timer = window.setTimeout(async () => {
            try {
                await handleDownloadPdf();
            } catch (err) {
                console.error('Auto-download failed:', err);
            } finally {
                onDownloadComplete?.();
            }
        }, 500);
        return () => window.clearTimeout(timer);
    }, [autoDownloadToken, dataLoaded, previewHasContent, onDownloadComplete]);

    if (silentMode) {
        return (
            <div className="sr-only opacity-0 pointer-events-none fixed inset-0 z-[-1] overflow-hidden" style={{ width: '210mm' }}>
                <InvoiceDocument ref={printAreaRef} variant="offscreen" {...documentProps} />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <style>{INVOICE_SCREEN_PRINT_CSS}</style>

            {/* Maquette 8b : la saisie à gauche, la facture à droite. L'aperçu vit sur le fond
                enfoncé de la charte, avec les actions sous lui — l'action principale ne disparaît
                donc jamais sous le pli du formulaire. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start print-hidden">
                <InvoiceForm {...formProps} />

                <div className="rounded-2xl border border-border bg-surface-soft p-[18px]">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Aperçu</span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-content-muted whitespace-nowrap shrink-0">
                            Couleur
                            <span className="w-3.5 h-3.5 rounded border border-border shrink-0" style={{ background: documentProps.accentColor }} />
                        </span>
                    </div>

                    <InvoiceDocument ref={printAreaRef} {...documentProps} />

                    {mentionsManquantes.length > 0 && (
                        <p role="alert" className="mt-3.5 text-sm rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-red-700 dark:text-red-300">
                            <b>Attention : envoi, impression et téléchargement impossibles, information légale manquante.</b> Il manque {mentionsManquantes.join(', ')}. À compléter dans les Paramètres de facturation (Informations légales).
                        </p>
                    )}

                    <div className="flex flex-col gap-2 mt-3.5">
                        <button
                            type="button"
                            onClick={handleOpenSendModal}
                            disabled={savingInvoice || sendingEmail}
                            className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white text-[13.5px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 whitespace-nowrap"
                        >
                            <Mail size={15} /> {sendingEmail ? 'Envoi en cours…' : 'Envoyer la facture'}
                        </button>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={handlePrintInvoice}
                                disabled={savingInvoice || sendingEmail}
                                className="flex-1 min-w-[130px] h-9 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-surface text-[12.5px] font-semibold text-content transition-colors hover:border-content disabled:opacity-60 whitespace-nowrap"
                            >
                                <Printer size={14} /> {savingInvoice ? 'Enregistrement…' : 'Imprimer'}
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadPdf}
                                disabled={savingInvoice || sendingEmail}
                                className="flex-1 min-w-[130px] h-9 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-surface text-[12.5px] font-semibold text-content transition-colors hover:border-content disabled:opacity-60 whitespace-nowrap"
                            >
                                <Download size={14} /> {savingInvoice ? 'Préparation…' : 'Télécharger'}
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="h-8 self-center inline-flex items-center gap-1.5 px-3 rounded-full text-[12px] font-semibold text-content-muted transition-colors hover:text-content whitespace-nowrap"
                        >
                            <RotateCcw size={13} /> Recommencer une facture vierge
                        </button>
                    </div>
                </div>
            </div>

            {/* Envoi en cours : l'écran reste visible derrière le voile, jamais masqué en blanc. */}
            {sendingEmail && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" style={{ background: 'rgba(21,22,26,.28)', backdropFilter: 'blur(2px)' }}>
                    <div className="rounded-2xl bg-surface border border-border px-6 py-5 text-center shadow-2xl">
                        <Loader2 size={20} className="mx-auto mb-2 animate-spin text-content-muted" />
                        <p className="text-[13.5px] font-semibold text-content">Envoi de la facture…</p>
                        <p className="text-[12px] text-content-muted mt-0.5">Le PDF est en cours de préparation.</p>
                    </div>
                </div>
            )}

            <InvoiceSendModal
                open={emailModalOpen}
                invoiceNumber={invoiceNumber}
                totalTtc={documentProps.totalTtcBrut}
                email={recipientEmail}
                onEmailChange={setRecipientEmail}
                sending={sendingEmail}
                onClose={() => setEmailModalOpen(false)}
                onSend={handleSendInvoiceEmail}
            />
        </div>
    );
};
